import React from 'react';

import { Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import {
  attendRegisterListSelector,
  sheetListSelector,
  yearSelector,
  monthSelector,
} from './selector';
import { getAttendRegisterList, getSheetList } from './thunk';

import type { TableBaseColumnType } from '@/components/TableBase/Body';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';

import TableBase from '@/components/TableBase';
import Title from '@/components/Title';
import { attendRegisterSlice } from '@/domain/attendRegister/redux';
import { toStringTimeDisplay } from '@/utils/date';

const getNextMonthDate = (year: number, month: number) => {
  const dnext = new Date(year, month);
  return {
    year: dnext.getFullYear(),
    month: dnext.getMonth() + 1,
  };
};

const getPrevMonthDate = (year: number, month: number) => {
  const dnext = new Date(year, month - 2);
  return {
    year: dnext.getFullYear(),
    month: dnext.getMonth() + 1,
  };
};

/**
 * プロパティ
 */
export type Props = {
  /** 子ども */
  child: ChildJson,
};

const AttendHistory: React.FC<Props> = ({
  child,
}) => {
  const dispatch = useDispatch();
  const attendRegisterList = useSelector(attendRegisterListSelector);
  const sheetList = useSelector(sheetListSelector);
  const year = useSelector(yearSelector);
  const month = useSelector(monthSelector);
  React.useEffect(() => {
    dispatch(getAttendRegisterList());
    dispatch(getSheetList());
  }, [dispatch]);
  const lastDate = new Date(year, month, 0).getDate();
  const rows = [];
  for (let i = 0; i < lastDate; i += 1) {
    const item = attendRegisterList.find((v) => {
      const v1 = new Date(v.data.attendDateTime);
      return v1.getFullYear() === year
        && v1.getMonth() === month - 1
        && v1.getDate() === i + 1
        && v.data.registerId === `${child.parentId}_${child.childId}`;
    });
    const sheet = sheetList.find((v) => {
      const v1 = new Date(v.data.attendanceDate);
      return v1.getFullYear() === year
        && v1.getMonth() === month - 1
        && v1.getDate() === i + 1
        && v.data.parentId === child.parentId
        && v.data.childId === child.childId;
    });
    let attendTime = '-';
    let leaveTime = '-';
    if (item) {
      attendTime = toStringTimeDisplay(new Date(item.data.attendDateTime), false);
      if (item.data.leaveDateTime) {
        leaveTime = toStringTimeDisplay(new Date(item.data.leaveDateTime), false);
      }
    }
    if (sheet) {
      if (sheet.data.attend) {
        attendTime = toStringTimeDisplay(new Date(sheet.data.attend.attendDateTime), false);
      }
      if (sheet.data.leave) {
        leaveTime = toStringTimeDisplay(new Date(sheet.data.leave.leaveDateTime), false);
      }
    }
    const d = new Date(year, month - 1, i + 1);
    const week = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
    rows.push({
      id: `day-${i + 1}`,
      index: `${i + 1}`,
      date: `${i + 1}日(${week})`,
      attendTime,
      leaveTime,
    });
  }
  const columns: TableBaseColumnType[] = [
    { id: 'date', label: '日', minWidth: 60 },
    { id: 'attendTime', label: '登園時刻', minWidth: 60 },
    { id: 'leaveTime', label: '降園時刻', minWidth: 60 },
  ];
  return (
    <div>
      <Title title="登降園履歴" />
      <div style={{ marginBottom: '10px' }}>
        <p style={{ textAlign: 'center' }}>
          <Button
            onClick={() => {
              dispatch(attendRegisterSlice.actions.setYearMonth(getPrevMonthDate(year, month)));
              dispatch(getAttendRegisterList());
            }}
            variant="contained"
            color="primary"
          >
            前の月
          </Button>
          <span style={{ margin: '0 10px' }}>{`${year}年${month}月`}</span>
          <Button
            onClick={() => {
              dispatch(attendRegisterSlice.actions.setYearMonth(getNextMonthDate(year, month)));
              dispatch(getAttendRegisterList());
            }}
            variant="contained"
            color="primary"
          >
            次の月
          </Button>
        </p>
        <TableBase
          rows={rows}
          columns={columns}
          actions={[]}
          isPagination={false}
          isShowFooter={false}
          size="small"
        />
      </div>
    </div>
  );
};

export default AttendHistory;
